import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { useFlags } from '@atlaskit/flag/use-flags';
import { fireOperationalAnalytics } from '@atlassian/analytics-bridge/fire-analytics/fire-analytics';
import { FetchError } from '@atlassian/help-center-common-util/fetch/fetch';
import {
	getRegistrationErrorAttributes,
	useManageAttachmentRegistration,
} from '@atlassian/proforma-form-renderer/attachment-registration';
import type { AttachmentUploadEndLifecycleEvent } from '@atlassian/proforma-form-renderer/lifecycle-handlers';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics/events';

import { useRegisterAsTemporaryAttachmentService } from '../../../../services/register-as-temporary-attachment';

import { messages } from './messages';

const ATTACHMENT_REGISTRY_FIX_GATE = 'proforma-jsdcloud-19146-attachment-registry-fix';

const isAttachmentRegistryFixEnabled = (): boolean =>
	FeatureGates.checkGate(ATTACHMENT_REGISTRY_FIX_GATE);

export const useAttachmentUploadEnd = () => {
	di(useFlags, useIntl, useManageAttachmentRegistration, useRegisterAsTemporaryAttachmentService);

	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fetch, error } = useRegisterAsTemporaryAttachmentService();
	const manageAttachmentRegistration = useManageAttachmentRegistration();

	useEffect(() => {
		if (!error) {
			return;
		}

		const showErrorFlag = () => {
			let description = formatMessage(messages.uploadErrorDescription);

			if (error instanceof FetchError && error.status === 400) {
				if (error.message) {
					description = error.message;
				}
			}

			showFlag({
				description,
				title: formatMessage(messages.uploadError),
				appearance: 'error',
			});
		};

		showErrorFlag();
	}, [error, showFlag, formatMessage]);

	return useCallback(
		(uploadEndPayload: AttachmentUploadEndLifecycleEvent) => {
			const fileId = uploadEndPayload.file.identifier.id;

			// Behind the gate the shared hook keeps the field blocked while registering and removes the
			// file on failure; without it we just register. Either way we fire FINISHED/FAILED analytics
			// so the registration step is monitored regardless of the gate. The handler is fire-and-forget
			// (returns void) because the renderer's lifecycle handler slot expects a void return.
			const registration = isAttachmentRegistryFixEnabled()
				? manageAttachmentRegistration(uploadEndPayload, () => fetch({ fileId }))
				: fetch({ fileId });

			registration
				.then(() => {
					fireOperationalAnalytics(
						createAnalyticsEvent({}),
						`${ActionSubject.UPLOAD_ISSUE_FORM_ATTACHMENTS} ${Action.FINISHED}`,
						{ fileId },
					);
				})
				.catch((registrationError: unknown) => {
					// The error flag is surfaced by the effect above which reacts to the service `error` state.
					fireOperationalAnalytics(
						createAnalyticsEvent({}),
						`${ActionSubject.UPLOAD_ISSUE_FORM_ATTACHMENTS} ${Action.FAILED}`,
						{
							fileId,
							failureType: 'attachmentRegistration',
							...getRegistrationErrorAttributes(registrationError),
						},
					);
				});
		},
		[fetch, manageAttachmentRegistration, createAnalyticsEvent],
	);
};
